import React, {useCallback, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {PageContainer} from '@/components/organisms';
import {HeaderTitle, Tab, TabsNavigation} from '@/components/atoms';
import {ButtonMenu} from '@/components/molecules';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {getSasToken} from '@/services/sasToken';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {Constants} from '@/utils/constants/commonConstants';
import {getFormattedDate} from '@/utils/commons';
import {logger} from '@/helpers/logger';
import {useWofQuestions} from '@/hooks/useWofQuestions';
import WofQuestionsUploadModal from './WofQuestionsUploadModal';
import WofQuestionsTable from './WofQuestionsTable';

const WheelOfFortuneAttributes = ({
  formData,
  handleFormChange,
  languages = []
}) => {
  const dispatch = useAppDispatch();
  const {uploadFileOnBlob} = useAzureBlob();
  const {handleDownloadTemplate} = useWofQuestions({languages});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuKey, setMenuKey] = useState(0);
  const pendingUploadRef = useRef(null);

  const activeLangCode = languages[activeTabIndex]?.code || '';

  const quizData = formData?.wofQuizQuestions;
  const currentFile = quizData?.quizFileName
    ? {name: quizData.quizFileName}
    : null;

  const recordsForTable = useMemo(() => {
    const questions = quizData?.data || [];
    return questions.map((q) => {
      const t = q.translations?.[activeLangCode] || {};
      return {
        question: t.question || '',
        rightAnswer: t.rightAnswer || '',
        wrongAnswer1: t.wrongAnswer1 || '',
        wrongAnswer2: t.wrongAnswer2 || '',
        wrongAnswer3: t.wrongAnswer3 || ''
      };
    });
  }, [quizData, activeLangCode]);

  const handleUpload = useCallback(
    async (file, formattedData) => {
      try {
        const sasResponse = await getSasToken(file.name, Constants.Quiz);

        if (!sasResponse?.blobUrl) {
          throw new Error('Upload URL not available');
        }

        const uploadResponse = await uploadFileOnBlob(sasResponse, file);

        if (uploadResponse?.message !== Constants.Success) {
          throw new Error('Upload failed');
        }

        const uploadedFileUrl =
          uploadResponse?.media?.imageUrl || sasResponse?.blobUrl || '';
        const updatedAt = new Date().toISOString();
        const updateDate = getFormattedDate(
          updatedAt,
          false,
          '-',
          'mm-dd-yyyy'
        );

        pendingUploadRef.current = {
          data: formattedData,
          quizFileName: file.name,
          quizFileUrl: uploadedFileUrl,
          updatedAt,
          fileName: file.name,
          lastUpdate: updateDate
        };

        return true;
      } catch (error) {
        logger.error('Quiz file upload failed:', error);

        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to upload file. Please try again.',
            type: 'error'
          })
        );

        return false;
      }
    },
    [dispatch, uploadFileOnBlob]
  );

  const handleUploadDone = useCallback(() => {
    if (pendingUploadRef.current) {
      handleFormChange({
        wofQuizQuestions: pendingUploadRef.current
      });
      pendingUploadRef.current = null;
    }

    dispatch(
      setToastNotifications({
        show: true,
        message: 'File uploaded successfully',
        type: 'success'
      })
    );
  }, [dispatch, handleFormChange]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    pendingUploadRef.current = null;
  }, []);

  const handleOpenUpload = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onDownloadTemplate = useCallback(() => {
    handleDownloadTemplate(quizData?.data || []);
  }, [handleDownloadTemplate, quizData]);

  const existingFileName = currentFile?.name || '';

  const menuOptions = [
    {id: 'download', label: 'Download Template'},
    {id: 'upload', label: 'Upload Filled Template'}
  ];

  const handleMenuChange = useCallback(
    (selectedOption) => {
      if (selectedOption?.id === 'download') {
        onDownloadTemplate();
      } else if (selectedOption?.id === 'upload') {
        handleOpenUpload();
      }
      setMenuKey((k) => k + 1);
    },
    [onDownloadTemplate, handleOpenUpload]
  );

  return (
    <PageContainer data-testid='wof-attributes-form'>
      <div data-testid='quiz-section'>
        <div>
          <HeaderTitle
            title='Questions'
            className='text-[16px] font-bold leading-[22px] text-[#FFFFFF]'
          />
        </div>

        <div className='flex flex-col gap-4'>
          <div className='flex justify-end'>
            <ButtonMenu
              key={menuKey}
              buttonProps={{
                text: 'Actions',
                variant: 'secondary',
                size: 'small',
                iconTrailing: 'expand_more'
              }}
              options={menuOptions}
              onChange={handleMenuChange}
              menuPosition='bottom-left'
            />
          </div>

          {languages.length > 1 && (
            <TabsNavigation
              className='border-b border-[color:var(--color-border-divider)]'
              activeTabIndex={activeTabIndex}
              handleSetActiveTab={setActiveTabIndex}>
              {languages.map((lang, idx) => (
                <Tab
                  key={lang.code}
                  label={lang.title}
                  onClick={() => setActiveTabIndex(idx)}
                  isDark
                />
              ))}
            </TabsNavigation>
          )}

          <WofQuestionsTable data={recordsForTable} />
        </div>
      </div>

      <WofQuestionsUploadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpload={handleUpload}
        onDone={handleUploadDone}
        onDownloadTemplate={onDownloadTemplate}
        existingFileName={existingFileName}
        languages={languages}
      />
    </PageContainer>
  );
};

WheelOfFortuneAttributes.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  languages: PropTypes.array
};

export default React.memo(WheelOfFortuneAttributes);
